import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  FolderOpen, ChevronDown, Plus, Check, RefreshCw, Loader2, ExternalLink, Trash2, AlertTriangle, Server,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

function ProjectStatusPill({ status }) {
  const cls =
    status === 'running' ? 'bg-sky-500/15 text-sky-200' :
    status === 'completed' ? 'bg-emerald-500/15 text-emerald-200' :
    status === 'error' || status === 'failed' ? 'bg-red-500/15 text-red-200' :
    'bg-zinc-500/15 text-zinc-300';
  return (
    <span className={`px-1.5 py-0.5 rounded text-[9px] font-medium uppercase tracking-wider ${cls}`}>
      {status || 'unknown'}
    </span>
  );
}

/**
 * ProjectSwitcher — the "My projects" dropdown in the Snagcart Builder header.
 * Lists every project the current user owns and lets them switch, open the
 * live preview, or delete each one.
 */
export default function ProjectSwitcher({ jobId, onSelect, onNew, onCreated, onDraft }) {
  const [open, setOpen] = useState(false);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  // Inline "create new project" form state.
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [includeBackend, setIncludeBackend] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const wrapRef = useRef(null);

  const authHeader = () => {
    const t = localStorage.getItem('token');
    return t ? { Authorization: `Bearer ${t}` } : {};
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/gpt-pilot/projects`, { headers: authHeader() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setProjects(Array.isArray(data) ? data : (data.projects || []));
    } catch {
      // switcher is non-essential; suppress toast
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);
  useEffect(() => {
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const deleteProject = useCallback(async (jid) => {
    if (!window.confirm('Delete this project and its workspace? This cannot be undone.')) return;
    try {
      const res = await fetch(`${API_URL}/api/gpt-pilot/projects/${jid}`, {
        method: 'DELETE',
        headers: authHeader(),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setProjects((p) => p.filter((x) => x.job_id !== jid));
      toast.success('Project deleted');
      if (jid === jobId) onNew();
    } catch (e) {
      toast.error(`Delete failed: ${e.message}`);
    }
  }, [jobId, onNew]);

  const recoverStuck = useCallback(async (jid) => {
    if (!window.confirm('Force this build to "interrupted"? The on-disk files are preserved; only the spinner is cleared.')) return;
    try {
      const res = await fetch(`${API_URL}/api/gpt-pilot/projects/${jid}/recover`, {
        method: 'POST',
        headers: authHeader(),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`);
      if (data.status === 'noop') {
        toast.message(`No action needed — project is already "${data.current_status}".`);
      } else {
        toast.success('Build marked as interrupted');
      }
      load();
    } catch (e) {
      toast.error(`Recover failed: ${e.message}`);
    }
  }, [load]);

  const createProject = useCallback(async () => {
    const name = newName.trim();
    if (!name) {
      toast.error('Project name is required');
      return;
    }
    // No backend call here — the project is "drafted" only. The actual
    // POST /api/gpt-pilot/projects fires when the user hits Send in the
    // chat composer (handled by the parent's onStartProject). This gives
    // the user a chance to refine the prompt before any tokens are spent.
    setSubmitting(true);
    try {
      const desc = newDesc.trim();
      setCreating(false);
      setNewName('');
      setNewDesc('');
      const wasBackend = includeBackend;
      setIncludeBackend(false);
      setOpen(false);
      if (onDraft) {
        // Hand the draft to the parent. Parent should populate the chat
        // composer + name field so the user can review and click Send.
        onDraft({ name, description: desc, include_backend: wasBackend });
        toast.success(`Drafted "${name}" — review the prompt and click Send to build`);
      } else if (onCreated) {
        // Legacy fallback — if the parent didn't supply onDraft we keep
        // the old behaviour of starting the build immediately.
        const body = { name, description: desc, include_backend: wasBackend };
        const res = await fetch(`${API_URL}/api/gpt-pilot/projects`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...authHeader() },
          body: JSON.stringify(body),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`);
        onCreated({ job_id: data.job_id, name, description: desc });
        toast.success(`Project "${name}" created`);
      }
      load();
    } catch (e) {
      toast.error(`Create failed: ${e.message}`);
    } finally {
      setSubmitting(false);
    }
  }, [newName, newDesc, includeBackend, onDraft, onCreated, load]);

  const current = projects.find((p) => p.job_id === jobId);

  return (
    <div ref={wrapRef} className="relative" data-testid="project-switcher">
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="project-switcher-toggle"
        className="px-3 py-1.5 rounded-lg bg-white/[0.04] hover:bg-white/[0.08] text-xs text-zinc-200 flex items-center gap-1.5 transition-colors"
        title="Switch between your projects"
      >
        <FolderOpen className="w-3.5 h-3.5 text-violet-300" />
        <span className="truncate max-w-[140px]">
          {current ? (current.name || current.job_id.slice(0, 8)) : 'My projects'}
        </span>
        <ChevronDown className="w-3 h-3 text-zinc-400" />
      </button>
      {open && (
        <div
          className="absolute right-0 mt-2 w-[360px] bg-[#0d0d0d] border border-white/[0.08] rounded-xl shadow-2xl z-50 overflow-hidden"
          data-testid="project-switcher-dropdown"
        >
          <div className="px-3 py-2 border-b border-white/[0.06] flex items-center justify-between">
            <span className="text-[11px] uppercase tracking-wider text-zinc-500">Your projects</span>
            <button
              onClick={() => setCreating((c) => !c)}
              data-testid="project-switcher-new"
              className="flex items-center gap-1 px-2 py-1 rounded text-[11px] text-violet-200 hover:bg-violet-500/15"
              title={creating ? 'Hide form' : 'Create a new project'}
            >
              <Plus className={`w-3 h-3 transition-transform ${creating ? 'rotate-45' : ''}`} />
              {creating ? 'Cancel' : 'New project'}
            </button>
          </div>
          {creating && (
            <div className="px-3 py-3 border-b border-white/[0.06] bg-white/[0.02] space-y-2" data-testid="project-create-form">
              <label className="block">
                <span className="text-[10px] uppercase tracking-wider text-zinc-500">Name</span>
                <input
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="e.g. Kolkata Quick Grocer"
                  data-testid="project-create-name"
                  className="mt-1 w-full px-2.5 py-1.5 rounded-md bg-black/40 border border-white/[0.08] text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-violet-400/60"
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); createProject(); } }}
                  autoFocus
                />
              </label>
              <label className="block">
                <span className="text-[10px] uppercase tracking-wider text-zinc-500">What should it do? (optional)</span>
                <textarea
                  value={newDesc}
                  onChange={(e) => setNewDesc(e.target.value)}
                  placeholder="One-line description used as the first build prompt."
                  rows={2}
                  data-testid="project-create-desc"
                  className="mt-1 w-full px-2.5 py-1.5 rounded-md bg-black/40 border border-white/[0.08] text-xs text-white placeholder:text-zinc-600 focus:outline-none focus:border-violet-400/60 resize-none"
                />
              </label>
              <label className="flex items-start gap-2 cursor-pointer select-none" data-testid="project-create-backend-toggle">
                <input
                  type="checkbox"
                  checked={includeBackend}
                  onChange={(e) => setIncludeBackend(e.target.checked)}
                  className="mt-0.5 accent-violet-500"
                />
                <span className="text-[11px] text-zinc-300 leading-snug">
                  <span className="inline-flex items-center gap-1 font-medium text-zinc-100">
                    <Server className="w-3 h-3 text-violet-300" />
                    Include backend (REST API + database)
                  </span>
                  <span className="block text-zinc-500 text-[10px] mt-0.5">
                    Uncheck for a static landing page / portfolio — the agent will skip the Express server.
                  </span>
                </span>
              </label>
              <button
                onClick={createProject}
                disabled={submitting || !newName.trim()}
                data-testid="project-create-submit"
                className="w-full px-3 py-1.5 rounded-md bg-violet-500 hover:bg-violet-400 disabled:opacity-40 disabled:cursor-not-allowed text-white text-xs font-semibold flex items-center justify-center gap-1.5 transition-colors"
                title="Open the chat with this prompt pre-filled. Build starts when you click Send."
              >
                {submitting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
                {submitting ? 'Drafting…' : 'Draft project & open chat'}
              </button>
              <p className="text-[10px] text-zinc-500 leading-snug -mt-0.5">
                You'll review the prompt in the chat and click <span className="text-zinc-300 font-medium">Send</span> to start the build.
              </p>
            </div>
          )}
          <div className="max-h-[420px] overflow-y-auto">
            {loading && (
              <div className="px-3 py-6 text-center text-zinc-500 text-xs">
                <Loader2 className="w-3.5 h-3.5 animate-spin inline mr-1.5" />
                Loading…
              </div>
            )}
            {!loading && projects.length === 0 && (
              <div className="px-3 py-6 text-center text-zinc-500 text-xs italic">No projects yet — start one below.</div>
            )}
            {!loading && projects.map((p) => {
              const active = p.job_id === jobId;
              return (
                <button
                  key={p.job_id}
                  onClick={() => { onSelect(p); setOpen(false); }}
                  data-testid={`project-switcher-item-${p.job_id.slice(0, 8)}`}
                  className={`w-full text-left px-3 py-2.5 hover:bg-white/[0.04] border-b border-white/[0.04] last:border-0 group ${active ? 'bg-violet-500/[0.08]' : ''}`}
                >
                  <div className="flex items-center gap-2">
                    {active && <Check className="w-3 h-3 text-violet-300 flex-shrink-0" />}
                    <span className="text-sm text-white font-medium truncate flex-1">{p.name || '(unnamed)'}</span>
                    <ProjectStatusPill status={p.status} />
                  </div>
                  <div className="flex items-center gap-2 mt-0.5 text-[10px] text-zinc-500">
                    <span className="font-mono">{p.job_id.slice(0, 8)}</span>
                    {p.created_at && (
                      <span>· {new Date(p.created_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                    )}
                    <span className="flex-1" />
                    <a
                      href={`${API_URL}/api/gpt-pilot/preview/${p.job_id}/`}
                      target="_blank"
                      rel="noreferrer noopener"
                      onClick={(e) => e.stopPropagation()}
                      className="text-zinc-400 hover:text-violet-200 flex items-center gap-0.5"
                      data-testid={`project-switcher-open-${p.job_id.slice(0, 8)}`}
                      title="Open this project's preview in a new tab"
                    >
                      <ExternalLink className="w-2.5 h-2.5" />
                      preview
                    </a>
                    <button
                      onClick={(e) => { e.stopPropagation(); deleteProject(p.job_id); }}
                      className="text-zinc-500 hover:text-red-300"
                      data-testid={`project-switcher-delete-${p.job_id.slice(0, 8)}`}
                      title="Delete this project + its workspace"
                    >
                      <Trash2 className="w-2.5 h-2.5" />
                    </button>
                    {['running', 'queued', 'building'].includes(p.status) && (
                      <button
                        onClick={(e) => { e.stopPropagation(); recoverStuck(p.job_id); }}
                        className="text-amber-400/80 hover:text-amber-200"
                        data-testid={`project-switcher-recover-${p.job_id.slice(0, 8)}`}
                        title="Build appears stuck — force-mark as interrupted"
                      >
                        <AlertTriangle className="w-2.5 h-2.5" />
                      </button>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
          <div className="px-3 py-2 border-t border-white/[0.06] flex items-center justify-between text-[10px] text-zinc-500">
            <span>{projects.length} project{projects.length === 1 ? '' : 's'}</span>
            <button onClick={load} className="hover:text-zinc-200" data-testid="project-switcher-refresh">
              <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
